export type Language =
  | "en" // English
  | "fr" // French
  | "it" // Italian
  | "de" // German
  | "es" // Spanish
  | "tr" // Turkish
  | "pt" // Portuguese (Brazilian)
  | "ru" // Russian
  | "zh" // Chinese (Simplified)
  | "ja" // Japanese
  | "ko"; // Korean

export interface Translations {
  // UI
  play: string;
  playing: string;
  buy: string;
  needMore: string;
  replay: string;
  nextPuzzle: string;
  collections: string;
  complete: string;
  collectionCompleted: string;
  levelOf: string; // "Level {current} of {total}"
  sound: string;
  language: string;

  // Collection names
  mixedCollection: string;
  natureLandscapes: string;
  foodDelights: string;
  oceanLife: string;
  cityArchitecture: string;
  cats: string;
  dogs: string;
  flowers: string;
  patterns: string;
  cottageCore: string;
  cars: string;
}

export const translations: Record<Language, Translations> = {
  en: {
    play: "Play",
    playing: "Playing",
    buy: "Buy",
    needMore: "Need more",
    replay: "Replay",
    nextPuzzle: "Next Puzzle",
    collections: "Collections",
    complete: "Complete",
    collectionCompleted: "Collection completed!",
    levelOf: "Level {current} of {total}",
    sound: "Sound",
    language: "Language",
    mixedCollection: "Mixed Collection",
    natureLandscapes: "Nature & Landscapes",
    foodDelights: "Food & Delights",
    oceanLife: "Ocean Life",
    cityArchitecture: "City & Architecture",
    cats: "Cats",
    dogs: "Dogs",
    flowers: "Flowers",
    patterns: "Patterns",
    cottageCore: "Cottage Core",
    cars: "Cars",
  },
  fr: {
    play: "Jouer",
    playing: "En cours",
    buy: "Acheter",
    needMore: "Plus de pièces",
    replay: "Rejouer",
    nextPuzzle: "Puzzle suivant",
    collections: "Collections",
    complete: "Terminé",
    collectionCompleted: "Collection terminée !",
    levelOf: "Niveau {current} sur {total}",
    sound: "Son",
    language: "Langue",
    mixedCollection: "Collection mixte",
    natureLandscapes: "Nature & Paysages",
    foodDelights: "Cuisine & Délices",
    oceanLife: "Vie marine",
    cityArchitecture: "Ville & Architecture",
    cats: "Chats",
    dogs: "Chiens",
    flowers: "Fleurs",
    patterns: "Motifs",
    cottageCore: "Cottage Core",
    cars: "Voitures",
  },
  it: {
    play: "Gioca",
    playing: "In gioco",
    buy: "Compra",
    needMore: "Servono più monete",
    replay: "Rigioca",
    nextPuzzle: "Prossimo puzzle",
    collections: "Collezioni",
    complete: "Completato",
    collectionCompleted: "Collezione completata!",
    levelOf: "Livello {current} di {total}",
    sound: "Suono",
    language: "Lingua",
    mixedCollection: "Collezione mista",
    natureLandscapes: "Natura & Paesaggi",
    foodDelights: "Cibo & Delizie",
    oceanLife: "Vita marina",
    cityArchitecture: "Città & Architettura",
    cats: "Gatti",
    dogs: "Cani",
    flowers: "Fiori",
    patterns: "Motivi",
    cottageCore: "Cottage Core",
    cars: "Auto",
  },
  de: {
    play: "Spielen",
    playing: "Läuft",
    buy: "Kaufen",
    needMore: "Mehr Münzen nötig",
    replay: "Nochmal",
    nextPuzzle: "Nächstes Puzzle",
    collections: "Sammlungen",
    complete: "Fertig",
    collectionCompleted: "Sammlung abgeschlossen!",
    levelOf: "Level {current} von {total}",
    sound: "Ton",
    language: "Sprache",
    mixedCollection: "Gemischte Sammlung",
    natureLandscapes: "Natur & Landschaften",
    foodDelights: "Essen & Genuss",
    oceanLife: "Meeresleben",
    cityArchitecture: "Stadt & Architektur",
    cats: "Katzen",
    dogs: "Hunde",
    flowers: "Blumen",
    patterns: "Muster",
    cottageCore: "Cottage Core",
    cars: "Autos",
  },
  es: {
    play: "Jugar",
    playing: "Jugando",
    buy: "Comprar",
    needMore: "Necesitas más",
    replay: "Repetir",
    nextPuzzle: "Siguiente puzzle",
    collections: "Colecciones",
    complete: "Completo",
    collectionCompleted: "¡Colección completada!",
    levelOf: "Nivel {current} de {total}",
    sound: "Sonido",
    language: "Idioma",
    mixedCollection: "Colección mixta",
    natureLandscapes: "Naturaleza y Paisajes",
    foodDelights: "Comida y Delicias",
    oceanLife: "Vida marina",
    cityArchitecture: "Ciudad y Arquitectura",
    cats: "Gatos",
    dogs: "Perros",
    flowers: "Flores",
    patterns: "Patrones",
    cottageCore: "Cottage Core",
    cars: "Coches",
  },
  tr: {
    play: "Oyna",
    playing: "Oynanıyor",
    buy: "Satın Al",
    needMore: "Daha fazla lazım",
    replay: "Tekrar Oyna",
    nextPuzzle: "Sonraki Yapboz",
    collections: "Koleksiyonlar",
    complete: "Tamamlandı",
    collectionCompleted: "Koleksiyon tamamlandı!",
    levelOf: "Seviye {current} / {total}",
    sound: "Ses",
    language: "Dil",
    mixedCollection: "Karışık Koleksiyon",
    natureLandscapes: "Doğa ve Manzaralar",
    foodDelights: "Yemek ve Lezzetler",
    oceanLife: "Okyanus Yaşamı",
    cityArchitecture: "Şehir ve Mimari",
    cats: "Kediler",
    dogs: "Köpekler",
    flowers: "Çiçekler",
    patterns: "Desenler",
    cottageCore: "Cottage Core",
    cars: "Arabalar",
  },
  pt: {
    play: "Jogar",
    playing: "Jogando",
    buy: "Comprar",
    needMore: "Precisa de mais",
    replay: "Jogar novamente",
    nextPuzzle: "Próximo quebra-cabeça",
    collections: "Coleções",
    complete: "Completo",
    collectionCompleted: "Coleção completa!",
    levelOf: "Nível {current} de {total}",
    sound: "Som",
    language: "Idioma",
    mixedCollection: "Coleção mista",
    natureLandscapes: "Natureza e Paisagens",
    foodDelights: "Comida e Delícias",
    oceanLife: "Vida marinha",
    cityArchitecture: "Cidade e Arquitetura",
    cats: "Gatos",
    dogs: "Cachorros",
    flowers: "Flores",
    patterns: "Padrões",
    cottageCore: "Cottage Core",
    cars: "Carros",
  },
  ru: {
    play: "Играть",
    playing: "Играем",
    buy: "Купить",
    needMore: "Нужно больше",
    replay: "Заново",
    nextPuzzle: "Следующий пазл",
    collections: "Коллекции",
    complete: "Готово",
    collectionCompleted: "Коллекция завершена!",
    levelOf: "Уровень {current} из {total}",
    sound: "Звук",
    language: "Язык",
    mixedCollection: "Смешанная коллекция",
    natureLandscapes: "Природа и пейзажи",
    foodDelights: "Еда и вкусности",
    oceanLife: "Морская жизнь",
    cityArchitecture: "Города и архитектура",
    cats: "Кошки",
    dogs: "Собаки",
    flowers: "Цветы",
    patterns: "Узоры",
    cottageCore: "Коттеджкор",
    cars: "Машины",
  },
  zh: {
    play: "开始",
    playing: "进行中",
    buy: "购买",
    needMore: "金币不足",
    replay: "重玩",
    nextPuzzle: "下一关",
    collections: "收藏集",
    complete: "完成",
    collectionCompleted: "收藏集已完成！",
    levelOf: "第 {current} 关，共 {total} 关",
    sound: "声音",
    language: "语言",
    mixedCollection: "混合收藏",
    natureLandscapes: "自然风光",
    foodDelights: "美食佳肴",
    oceanLife: "海洋生物",
    cityArchitecture: "城市建筑",
    cats: "猫咪",
    dogs: "狗狗",
    flowers: "花卉",
    patterns: "图案",
    cottageCore: "田园风",
    cars: "汽车",
  },
  ja: {
    play: "プレイ",
    playing: "プレイ中",
    buy: "購入",
    needMore: "コイン不足",
    replay: "リプレイ",
    nextPuzzle: "次のパズル",
    collections: "コレクション",
    complete: "完了",
    collectionCompleted: "コレクション完了！",
    levelOf: "レベル {current} / {total}",
    sound: "サウンド",
    language: "言語",
    mixedCollection: "ミックス",
    natureLandscapes: "自然と風景",
    foodDelights: "フード",
    oceanLife: "海の生き物",
    cityArchitecture: "都市と建築",
    cats: "猫",
    dogs: "犬",
    flowers: "花",
    patterns: "パターン",
    cottageCore: "コテージコア",
    cars: "車",
  },
  ko: {
    play: "플레이",
    playing: "플레이 중",
    buy: "구매",
    needMore: "코인 부족",
    replay: "다시하기",
    nextPuzzle: "다음 퍼즐",
    collections: "컬렉션",
    complete: "완료",
    collectionCompleted: "컬렉션 완료!",
    levelOf: "레벨 {current} / {total}",
    sound: "소리",
    language: "언어",
    mixedCollection: "믹스 컬렉션",
    natureLandscapes: "자연과 풍경",
    foodDelights: "음식",
    oceanLife: "바다 생물",
    cityArchitecture: "도시와 건축",
    cats: "고양이",
    dogs: "강아지",
    flowers: "꽃",
    patterns: "패턴",
    cottageCore: "코티지코어",
    cars: "자동차",
  },
};

// Map collection IDs to translation keys
export const collectionNameKeys: Record<string, keyof Translations> = {
  mixed: "mixedCollection",
  "nature-landscapes": "natureLandscapes",
  "food-delights": "foodDelights",
  "ocean-life": "oceanLife",
  "city-architecture": "cityArchitecture",
  cat: "cats",
  dogs: "dogs",
  flowers: "flowers",
  patterns: "patterns",
  "cottage-core": "cottageCore",
  cars: "cars",
};
